import { AfterViewInit, Component, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { GprMaterialsV1Service, GprPrintParameterDto, MpgValuePerMainElementDto, NmdCalculationResponseDto, NmdCalculationResponseWrapperDto, PrintResultDto, ProjectDto, ValuePerPhasePerEnvironmentalEffectDto } from '../core/services/GPRMV1Service.Generated';
import { AppService } from '../core/services/app.service';
import { PerPhaseNames } from '../pages/result/perphase-names';
import { ENmdProductStatus } from '../shared/enums/nmd-product-status.enum';
import { FeatureManagementService } from '../core/services/feature-management.service';
import { DataSetSettingEnum } from '../shared/enums/data-set-setting.enum';
import { CombinationSpecificParamNamesEnum } from '../shared/enums/combination-specific-param-names.enum';
import { TranslateService } from '@ngx-translate/core';
import { ValuePerPhaseModel } from '../shared/models/value-per-phase.model';
import { EnvironmentEffects } from '../pages/result/environment-effects';
import { Indicators } from '../pages/result/indicators';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { EMPTY, Observable, Subject } from 'rxjs';

class MainElementDto {
    readonly colorCode: string;
    readonly id: number;
    readonly name: string;
    readonly value: number;
    readonly percentage: number;

    constructor(id: number, name: string, value: number, colorCode: string, percentage: number) {
        this.colorCode = colorCode;
        this.id = id;
        this.name = name;
        this.value = value;
        this.percentage = percentage;
    }
}

@Component({
    selector: 'app-print-page',
    templateUrl: './print-page.component.html',
    styleUrls: ['./print-page.component.scss'],
    standalone: false
})
export class PrintPageComponent implements AfterViewInit, OnDestroy {

    environmentEffects: ValuePerPhaseModel[];
    environmentEffectsAfter: ValuePerPhaseModel[];

    indicators: ValuePerPhaseModel[];
    indicatorsAfter: ValuePerPhaseModel[];
    isExistingBuildingCal: boolean;
    existingBuildingParameters: GprPrintParameterDto[] = [];
    calculationResults: NmdCalculationResponseDto;
    calculationResultsAfter: NmdCalculationResponseDto;

    nmdCalculationResponseDto_ForExtraIndicator: NmdCalculationResponseDto;

    calculationName: string;
    printData: PrintResultDto;
    math = Math;
    dataSetSetting = DataSetSettingEnum;
    selectedDataSet: number;
    mpgResultTitle: string;
    mpgResultTitlePerMainElementTitle: string
    elementResultTitle: string;
    envirnmentalCategoryTitle: string;

    projectCharacteristics: ProjectDto;
    nmdProductStatusEnum = ENmdProductStatus;
    uploadsImageBaseUrl: string = environment.uploadsBaseUrl;
    climateChangeGWP: number = 0;
    resterendLevensduurIngreep: number = 0;
    levensduurIngreep: number = 0;
    resterendLevensduurHuidig: number = 0;
    levensduurHuidig: number = 0;
    selectedsubmenu: string;

    // MPG card
    mpgColumns: string[] = ['colorCode', 'name', 'value', 'percentage'];

    // MKI card
    mkiColumns: string[] = ['name', 'mki'];

    tableColumns: string[] = ['allphases', 'unit'];
    codes = {};

    currentMainElements: MainElementDto[] = [];
    afterMainElements: MainElementDto[] = [];
    colors = [
        '#FF7A00',
        '#FFB500',
        '#FF5257',
        '#DB021F',
        '#ABB4F1',
        '#333E86',
        '#F9D5D6',
        '#FF61A9',
        '#00BBB9',
        '#00B33D',
        '#96DC00',
        '#F458F8',
        '#EA16A2',
        '#FFD600'
    ]

    // Chart config options
    chartOption = {
        gradient: false,
        showLegend: false,
        showLabels: false,
        isDoughnut: true,
        tooltipDisabled: true,
        arcWidth: 0.40,
        view: [200, 200],
        legendPosition: 'left',
        legendTitle: '',
        colorScheme: {
            domain: this.colors
        }
    }
    private _destroySubscription: Subject<boolean> = new Subject();
    isShowIndicatorPage: boolean = false;
    isProjectPhaseUIEnabled = false;
    isPreview: boolean = false;

    constructor(private _router: Router, private activatedRoute: ActivatedRoute, private appService: AppService, private _gprMaterialsService: GprMaterialsV1Service,
        private _featureService: FeatureManagementService, private _translate: TranslateService,
        private _cdr: ChangeDetectorRef) {
        this.isExistingBuildingCal = this.appService.isExistingBuildingCalculation;

        // Read any query/route params that may already be present (important when opened
        // directly in a new tab). This ensures `selectedsubmenu` / `isPreview` are set
        // before route `data` arrives so the template `*ngIf` conditions evaluate
        // correctly.
        this.selectedsubmenu = this.activatedRoute?.snapshot?.queryParams?.['type'] || this.activatedRoute?.snapshot?.params?.['type'] || this.activatedRoute?.parent?.snapshot?.params?.['type'];
        this.isPreview = this.activatedRoute?.snapshot?.queryParams?.['preview'] === 'true' || this.activatedRoute?.snapshot?.queryParams?.['preview'] === true;

        if (this.activatedRoute?.data && this.activatedRoute.data.subscribe) {
            this.activatedRoute.data.pipe(takeUntil(this._destroySubscription)).subscribe((data: { printData: PrintResultDto }) => {
                this.printData = data.printData;
                this.calculationName = this.printData.calculationName;
                this.projectCharacteristics = this.printData.projectCharacteristics;
                this.selectedDataSet = this.appService.getDatasetChoiceId();
                this.createMainElementGraphList();

                if (this.projectCharacteristics === undefined) {
                    this.projectCharacteristics = new ProjectDto();
                }

                this.setCalculationResults();
                this.setHeaderTitles();
                this.mappingPerPhasePer(this.calculationResults);
                this.mappingPerPhasePer(this.calculationResultsAfter);
                // ensure view updates when data arrives (helps when opened in new tab)
                // schedule detection asynchronously to avoid running change detection
                // while Angular is still in creation mode (which triggers an assertion).
                Promise.resolve().then(() => this._cdr.detectChanges());
            });
        }
    }


    ngOnInit() {
        this._featureService.isProjectPhaseUIEnabled()
            .pipe(takeUntil(this._destroySubscription))
            .subscribe(resp => { this.isProjectPhaseUIEnabled = resp; });

        this.activatedRoute.queryParams.pipe(takeUntil(this._destroySubscription)).subscribe(params => {
            this.selectedsubmenu = params['type'];
            this.isPreview = params['preview'] === 'true' || params['preview'] === true;

            // schedule detection asynchronously to prevent "Should be run in update mode" assertion
            Promise.resolve().then(() => this._cdr.detectChanges());
        });

        this.calculationResults?.valuePerPhasePerEnvironmentalEffectCategory?.slice().reverse().forEach(item => {
            this.tableColumns.unshift(item.code);
            const shortName = PerPhaseNames.find(p => p.code === item.code);
            this.codes[item.code] = shortName ? shortName.shortName : '';
        });

        this.tableColumns.unshift('name');
        this.codes['allphases'] = this._translate.instant('RESULT.ALL_PHASES');
        this.codes['unit'] = this._translate.instant('RESULT.UNIT');

        this.getCalculationCoreResults()
            .pipe(takeUntil(this._destroySubscription))
            .subscribe(() => {
                if (!this.isExistingBuildingCal) {
                    if (this.selectedDataSet == this.dataSetSetting.A1A2) {
                        this.nmdCalculationResponseDto_ForExtraIndicator = this.calculationResultsAfter;
                    } else if (this.selectedDataSet == this.dataSetSetting.A1) {
                        this.nmdCalculationResponseDto_ForExtraIndicator = this.calculationResults;
                    } else {
                        this.nmdCalculationResponseDto_ForExtraIndicator = this.calculationResults;
                    }
                }

            });

        this.getShowIndicatorPageFeature();
    }

    ngOnDestroy(): void {
        this._destroySubscription.next(true);
        this._destroySubscription.complete();
    }

    /**
   * To get the NmdCalculationResponseDto from API call 
   */
    getCalculationCoreResults(): Observable<NmdCalculationResponseWrapperDto> {
        // Guard: ensure a calculation id is available before calling the API.
        let calculationId: number = null;
        try {
            calculationId = this.appService.getCalculationId();
        } catch (err) {
            calculationId = null;
        }

        if (!calculationId) {
            return EMPTY as unknown as Observable<NmdCalculationResponseWrapperDto>;
        }

        return this._gprMaterialsService
            .getCalculationResultsForNewAndExistingBuildings(
                calculationId,
                this.isExistingBuildingCal
            )
            .pipe(
                tap((response: NmdCalculationResponseWrapperDto) => {
                    this.applyCalculationResultsFromSource(response);

                    // Mapping per phase
                    this.mappingPerPhasePer(this.calculationResults);
                    this.mappingPerPhasePer(this.calculationResultsAfter);
                }),
                catchError((err) => {
                    this.calculationResults = null;
                    return EMPTY; // swallow error so UI can still render partial data
                })
            );
    }

    /**
     * apply calculation results to the component properties based on the calculation type (existing or new building) and selected dataset.
     * @param response
     */
    private applyCalculationResultsFromSource(response: NmdCalculationResponseWrapperDto) {
        // For existing building
        if (this.isExistingBuildingCal) {
            if (this.selectedDataSet == this.dataSetSetting.A1) {
                this.calculationResults = response.existingBuildingCurrentResultsA1;
                this.calculationResultsAfter = response.existingBuildingAfterResultsA1;
            } else if (this.selectedDataSet == this.dataSetSetting.A2) {
                this.calculationResults = response.existingBuildingCurrentResultsA2;
                this.calculationResultsAfter = response.existingBuildingAfterResultsA2;
            }
        }
        // For new building
        else {
            if (this.selectedDataSet == this.dataSetSetting.A1A2) {
                this.calculationResults = response.newBuildingResultsA1;
                this.calculationResultsAfter = response.newBuildingResultsA2;
            } else if (this.selectedDataSet == this.dataSetSetting.A1) {
                this.calculationResults = response.newBuildingResultsA1;
            } else {
                this.calculationResults = response.newBuildingResultsA2;
            }
        }

        // Calling to create lists for categories
        this.prepareEnvironmentEffectsCategory();
        this.prepareIndicatorsCategory();
    }

    /**
     * Maps the names of `mpgPerPhase` and `mkiPerPhase` objects in the `NmdCalculationResponseDto` 
     * based on their corresponding `code` values from the `PerPhaseNames` array.
     * @param {NmdCalculationResponseDto} nmdCalculationResponseDto - The input object containing `mpgPerPhase` and `mkiPerPhase` arrays.
     */
    mappingPerPhasePer(nmdCalculationResponseDto: NmdCalculationResponseDto) {
        if (nmdCalculationResponseDto) {
            nmdCalculationResponseDto.mpgPerPhase.forEach((mpgPerPhase) => {
                const mpgPerPhaseName = PerPhaseNames.find(p => p.code === mpgPerPhase.code);
                mpgPerPhase['name'] = mpgPerPhaseName ? mpgPerPhaseName.name : '';
            });

            nmdCalculationResponseDto.mkiPerPhase.forEach((mkiPerPhase) => {
                const mkiPerPhaseName = PerPhaseNames.find(p => p.code === mkiPerPhase.code);
                mkiPerPhase['name'] = mkiPerPhaseName ? mkiPerPhaseName.name : '';
            });

            if (nmdCalculationResponseDto.gwp50PerPhase) {
                nmdCalculationResponseDto.gwp50PerPhase.forEach((gwp50PerPhase) => {
                    const gwp50PerPhaseName = PerPhaseNames.find(p => p.code === gwp50PerPhase.code);
                    gwp50PerPhase['name'] = gwp50PerPhaseName ? gwp50PerPhaseName.name : '';
                })
            }
        }
    }

    /**
     * Calculating lifespan for existing building inputs
     */
    setLifeSpansValuesForExistingBuilding() {
        let bouwjaarHuidigValue = 0;
        let verWatchSloojaarHuidigValue = 0;
        let jaarVanIngreepValue = 0;
        let verWatchSloojaarIngreepValue = 0;

        this.printData.mainCharacteristics
            .forEach(main => main.existingBuildingElements
                .forEach(existingBuilding => existingBuilding.parameters
                    .forEach(parameter => {
                        // huidig user input
                        if (existingBuilding.elementName === 'Huidig') {
                            if (parameter.name == 'Bouwjaar') {
                                bouwjaarHuidigValue = +parameter.value;
                            }
                            if (parameter.name == 'Verwacht Sloopjaar') {
                                verWatchSloojaarHuidigValue = +parameter.value;
                            }
                        }

                        // ingreep user input
                        if (existingBuilding.elementName === 'Ingreep') {
                            if (parameter.name == 'Jaar Van Ingreep') {
                                jaarVanIngreepValue = +parameter.value;
                            }
                            if (parameter.name == 'Verwacht Sloopjaar') {
                                verWatchSloojaarIngreepValue = +parameter.value;
                            }
                        }
                    })
                )
            );

        this.levensduurHuidig = verWatchSloojaarHuidigValue - bouwjaarHuidigValue;
        this.resterendLevensduurHuidig = verWatchSloojaarHuidigValue - jaarVanIngreepValue;
        this.levensduurIngreep = verWatchSloojaarIngreepValue - bouwjaarHuidigValue;
        this.resterendLevensduurIngreep = verWatchSloojaarIngreepValue - jaarVanIngreepValue;
    }

    ngAfterViewInit() {
        if (!this.isPreview) {
            setTimeout(() => {
                window.print();
                this._router.navigate([{ outlets: { print: null } }]);
            }, 3000);
        }
    }

    /**
   * Creates the main element graph list.
   * @return {void} No return value.
   */
    createMainElementGraphList() {
        this.currentMainElements = (this.printData.mpgValuePerMainElements || []).map((mainElement: MpgValuePerMainElementDto, index) => {
            const mpgPercentage = this.calculatePercentage(mainElement.mpg, this.printData.totalMpg);

            return new MainElementDto(mainElement.mainElementId, mainElement.name, mainElement.mpg, this.colors[index], mpgPercentage);
        });

        if (this.isExistingBuildingCal || (!this.isExistingBuildingCal && this.selectedDataSet === this.dataSetSetting.A1A2)) {
            this.afterMainElements = (this.printData.mpgValuePerMainElements || []).map((mainElement: MpgValuePerMainElementDto, index) => {
                const mpgPercentage = this.calculatePercentage(mainElement.mpgAfter, this.printData.totalMpgAfter);

                return new MainElementDto(mainElement.mainElementId, mainElement.name, mainElement.mpgAfter, this.colors[index], mpgPercentage);
            });
        }
    }

    /**
   * Calculate percentage
   * @param partialValue 
   * @param totalValue 
   * @returns 
   */
    calculatePercentage(partialValue: number, totalValue: number): number {
        if (totalValue === 0) {
            return 0;
        }
        return +((100 * partialValue) / totalValue).toFixed();
    }

    /**
    * Set Calculation results based on dataSet setting and calculation type.
    */
    setCalculationResults() {
        this.applyCalculationResultsFromSource(this.printData.calculationResults);
        if (this.isExistingBuildingCal) {
            this.setLifeSpansValuesForExistingBuilding();
        }
    }

    /**
  * Get toggle event and creating indicators category list for table
  * @param isAfterResult 
  */
    prepareIndicatorsCategory(isAfterResult: boolean = false) {
        this.indicators = [];
        this.indicatorsAfter = [];

        try {
            if (isAfterResult) {
                this.calculationResultsAfter.valuePerPhasePerEnvironmentalEffectCategory.forEach((valuePerPhase, index) => {
                    this.prepareIndicators(valuePerPhase, index, true);
                });

            } else {
                this.calculationResults.valuePerPhasePerEnvironmentalEffectCategory.forEach((valuePerPhase, index) => {
                    this.prepareIndicators(valuePerPhase, index, false);
                });
            }

            this.calculateAllPhases(this.indicators);

        } catch (error) {
        }
    }

    /**
 * Prepare indicators for a given value per phase and index.
 * @param valuePerPhase: Single object from valuePerPhasePerEnvironmentalEffectCategory list
 * @param index: Index of valuePerPhasePerEnvironmentalEffectCategory list
 */
    prepareIndicators(valuePerPhase: ValuePerPhasePerEnvironmentalEffectDto, index: number, isAfter: boolean) {
        Indicators.forEach((indicator, eeIndex) => {
            let obj: ValuePerPhaseModel = {};
            if (index === 0) {
                isAfter ? this.indicatorsAfter.push(obj) : this.indicators.push(obj);
            } else {
                obj = isAfter ? this.indicatorsAfter[eeIndex] : this.indicators[eeIndex];
            }
            obj.name = indicator.name;
            obj.unit = indicator.unit;

            const matchedIndicator = valuePerPhase.indicators.find(item => item.id === indicator.id);
            if (matchedIndicator) {
                obj[valuePerPhase.code] = matchedIndicator.value;
            } else {
                obj[valuePerPhase.code] = 0;
            }
        });
    }

    /**
 * creating environment effects category list for table
 */
    prepareEnvironmentEffectsCategory() {
        this.environmentEffects = [];
        this.environmentEffectsAfter = [];

        try {

            if (this.calculationResultsAfter != undefined) {
                this.calculationResultsAfter?.valuePerPhasePerEnvironmentalEffectCategory.forEach((valuePerPhase, index) => {
                    this.prepareEnvironmentEffects(valuePerPhase, index, true);
                });
                this.calculationResultsAfter ? this.addMkiDetailsWithTotalMpgValue(true) : this.addMkiDetailsWithTotalMpgValue(false);
                this.calculateAllPhases(this.environmentEffectsAfter);
            }

            this.calculationResults?.valuePerPhasePerEnvironmentalEffectCategory.forEach((valuePerPhase, index) => {
                this.prepareEnvironmentEffects(valuePerPhase, index, false);
            });
            this.calculationResults ? this.addMkiDetailsWithTotalMpgValue(false) : this.addMkiDetailsWithTotalMpgValue(true);
            this.calculateAllPhases(this.environmentEffects);

        } catch (error) {
        }
    }

    /**
 * Calculating or sum of the all phases into a single value (allphases)
 * @param arrayList: It may take environmental effect or indicator array list
 */
    calculateAllPhases(arrayList: ValuePerPhaseModel[]) {
        if (arrayList.length > 0) {
            const codes = Object.keys(arrayList[0]).filter(key => !['name', 'unit', 'allphases'].includes(key))

            arrayList.forEach((element: ValuePerPhaseModel) => {
                element.allphases = codes.reduce((total, code) => total + element[code], 0);
            });
        }
    }

    /**
 * A function that prepares the environment effects based on the given value per phase and index.
 * @param valuePerPhase - The value per phase data.
 * @param index - The index of the current value per phase.
 * @param isAfter - The isAfter for CalculationResult.
 */
    prepareEnvironmentEffects(valuePerPhase: ValuePerPhasePerEnvironmentalEffectDto, index: number, isAfter: boolean) {
        let totalEnvCount = valuePerPhase.environmentEffects.length;
        let envEffects = this.getEnvironmentEffects(totalEnvCount);
        envEffects.forEach((environmentEffect, eeIndex) => {
            let obj: ValuePerPhaseModel = {};
            if (index === 0) {
                isAfter ? this.environmentEffectsAfter.push(obj) : this.environmentEffects.push(obj);
            }
            else {
                obj = isAfter ? this.environmentEffectsAfter[eeIndex] : this.environmentEffects[eeIndex];
            }
            obj.name = environmentEffect.name;
            obj.unit = environmentEffect.unit;

            const ee = valuePerPhase.environmentEffects.find(e => e.id === environmentEffect.id);

            if (ee) {
                obj[valuePerPhase.code] = ee.value;
            }
            else {
                // klimaatverandering - totaal(39) of 40, 41 and 42.
                if (this.selectedDataSet != this.dataSetSetting.A1 && environmentEffect.id == 39) {
                    const eeForFossiel = valuePerPhase.environmentEffects.find(e => e.id === 40); // klimaatverandering - fossiel
                    const eeForbiogeen = valuePerPhase.environmentEffects.find(e => e.id === 41); // klimaatverandering - biogeen
                    const eeForLandgebruik = valuePerPhase.environmentEffects.find(e => e.id === 42); // klimaatverandering - landgebruik en verandering landgebruik

                    obj[valuePerPhase.code] = eeForFossiel.value + eeForbiogeen.value + eeForLandgebruik.value;
                } else {
                    obj[valuePerPhase.code] = 0;
                }
            }
        });
    }

    /**
  * Getting EnvironmentEffects filter by selected assessmentStrategyId
  * @returns
  */
    getEnvironmentEffects(totalEnvCount: number = 0) {
        return (this.selectedDataSet == this.dataSetSetting.A1A2)
            ? EnvironmentEffects.filter(effect => effect.assessmentStrategyId === (totalEnvCount == EnvironmentEffects.filter(effect => effect.assessmentStrategyId === this.dataSetSetting.A1).length ? this.dataSetSetting.A1 : this.dataSetSetting.A2))
            : EnvironmentEffects.filter(effect => effect.assessmentStrategyId === this.selectedDataSet);
    }

    /**
  * Adding a data of MKI into environmental effect array list
  */
    addMkiDetailsWithTotalMpgValue(isAfterResult?: boolean) {
        const nmdCalculationResponseDto = isAfterResult ? this.calculationResultsAfter : this.calculationResults;
        const obj = {};

        obj['name'] = this._translate.instant('RESULT.MKI_HEADER');
        obj['unit'] = '';
        obj['allphases'] = nmdCalculationResponseDto.mki;
        nmdCalculationResponseDto.mkiPerPhase.forEach(item => {
            obj[item.code] = item.mki;
        });

        isAfterResult ? this.environmentEffectsAfter.push(obj) : this.environmentEffects.push(obj);
    }

    /**
  * Set header titles based on dataSet setting
  */
    setHeaderTitles() {
        if (this.selectedDataSet === DataSetSettingEnum.A1) {
            this.mpgResultTitle = this._translate.instant('PRINT_PAGE.MPG_RESULTSA1');
            this.mpgResultTitlePerMainElementTitle = this._translate.instant('PRINT_PAGE.MPG_RESULT_PER_MAIN_ELEMENTA1');
            this.elementResultTitle = this._translate.instant('PRINT_PAGE.ELEMENT_A1');
        }
        else if (this.selectedDataSet === DataSetSettingEnum.A2) {
            this.mpgResultTitle = this._translate.instant('PRINT_PAGE.MPG_RESULTSA2');
            this.mpgResultTitlePerMainElementTitle = this._translate.instant('PRINT_PAGE.MPG_RESULT_PER_MAIN_ELEMENTA2');
            this.elementResultTitle = this._translate.instant('PRINT_PAGE.ELEMENT_A2');
        }
        else {
            this.mpgResultTitle = this._translate.instant('PRINT_PAGE.MPG_RESULTSA1ANDA2');
            this.mpgResultTitlePerMainElementTitle = this._translate.instant('PRINT_PAGE.MPG_RESULT_PER_MAIN_ELEMENTA1ANDA2');
            this.elementResultTitle = this._translate.instant('PRINT_PAGE.ELEMENT_A1ANDA2');
        }
        this.envirnmentalCategoryTitle = this._translate.instant('PRINT_PAGE.ENVIRONMENTALCATEGORY');
    }

    /**
    * get the ShowIndicatorPage feature flag
    */
    getShowIndicatorPageFeature() {
        this._featureService.isShowIndicatorPage().pipe(takeUntil(this._destroySubscription)).subscribe(resp => this.isShowIndicatorPage = resp);
    }

    /**
     * Checks if the current calculation is for a combination building.
     * @returns True if combination building is selected, false otherwise
     */
    get isCombinationBuildingSelected(): boolean {
        if (!this.printData?.mainCharacteristics) {
            return false;
        }

        return this.printData.mainCharacteristics.some(characteristic => {
            const buildingUseFunction = characteristic.parameters?.find(p => 
                this.normalizeKey(p.name) === this.normalizeKey('Gebruiksfunctie')
            );
            
            if (buildingUseFunction) {
                const normalizedValue = this.normalize(buildingUseFunction.value);
                return normalizedValue === 'combinatiegebouw';
            }
            
            return false;
        });
    }

    /**
     * Filters parameters to exclude combination building-specific parameters when displaying
     * building characteristics for combination buildings.
     * @param parameters Array of parameters to filter
     * @returns Filtered array of parameters
     */
    getFilteredParameters(parameters: GprPrintParameterDto[]): GprPrintParameterDto[] {
        if (!parameters) {
            return [];
        }


        const normalizedCombinationParams = Object.values(CombinationSpecificParamNamesEnum).map(name =>
            this.normalizeKey(name)
        );

        // Filter out combination-specific parameters
        return parameters.filter(param => {
            const normalizedParamName = this.normalizeKey(param.name);
            
            // Check if the parameter name matches any of the combination-specific parameters
            return !normalizedCombinationParams.some(combParam => 
                normalizedParamName === combParam || 
                normalizedParamName.includes(combParam) || 
                combParam.includes(normalizedParamName)
            );
        });
    }

    /**
     * Normalizes a value by converting to lowercase and trimming whitespace.
     * @param value The value to normalize
     * @returns Normalized string
     */
    private normalize(value: unknown): string {
        return String(value ?? '').trim().toLowerCase();
    }

    /**
     * Normalizes a key by removing all non-alphanumeric characters.
     * @param value The value to normalize
     * @returns Normalized key string
     */
    private normalizeKey(value: unknown): string {
        return String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    }
}
